import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';
import { FaCalendarAlt, FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Print styles only - original screen styles remain unchanged
const printStyles = `
  @media print {
    @page {
      size: A4 landscape;
      margin: 1cm;
    }
    
    body { 
      margin: 0 !important; 
      padding: 20px !important; 
      font-family: Arial, sans-serif !important; 
    }
    
    .print-header { 
      text-align: center !important; 
      margin-bottom: 20px !important; 
      border-bottom: 2px solid #333 !important; 
      padding-bottom: 10px !important; 
    }
    
    .print-title { 
      font-size: 20px !important; 
      font-weight: bold !important; 
      margin-bottom: 5px !important; 
    }
    
    .print-subtitle { 
      font-size: 12px !important; 
      color: #666 !important; 
      margin-bottom: 10px !important; 
    }
    
    .no-print, 
    .search-container,
    .filter-buttons-row,
    .filters-container {
      display: none !important;
    }
    
    .data-table { 
      width: 100% !important; 
      border-collapse: collapse !important; 
      margin-bottom: 20px !important; 
      font-size: 8px !important; 
    }
    
    .data-table th { 
      background-color: #e9ecef !important; 
      border: 1px solid #dee2e6 !important; 
      padding: 4px 2px !important; 
      text-align: left !important; 
      font-weight: bold !important;
      font-size: 7px !important;
      line-height: 1.2 !important;
      white-space: nowrap !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .data-table td { 
      border: 1px solid #dee2e6 !important; 
      padding: 3px 2px !important;
      font-size: 7px !important;
      line-height: 1.1 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
    
    .data-table .text-right { 
      text-align: right !important; 
    }
    
    .data-table .text-center { 
      text-align: center !important; 
    }
    
    .max-w-8xl {
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: none !important;
    }
    
    .overflow-x-auto {
      overflow: visible !important;
    }
  }
  
  /* Original screen styles - unchanged */
  .contract-table th,
  .contract-table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .contract-table td:hover {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .overflow-x-auto::-webkit-scrollbar {
    height: 4px;
  }

  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #0c75b2;
    border-radius: 2px;
  }

  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #095a8a;
  }

  .address-cell {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    cursor: help;
  }

  .search-container {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
  }

  .search-input {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    transition: border-color 0.2s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: #0c75b2;
    box-shadow: 0 0 0 3px rgba(12, 117, 178, 0.1);
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .filter-toggle-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 2px solid #0c75b2;
    border-radius: 6px;
    background: white;
    color: #0c75b2;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;
    justify-content: center;
  }

  .filter-toggle-button:hover {
    background: #f0f9ff;
  }

  .filter-toggle-button.view-no-policy {
    background: white;
    color: #0c75b2;
    border-color: #0c75b2;
  }

  .filter-toggle-button.view-no-policy:hover {
    background: #f0f9ff;
  }

  .filter-toggle-button.view-all-records {
    background: #f97316;
    color: white;
    border-color: #f97316;
  }

  .filter-toggle-button.view-all-records:hover {
    background: #ea580c;
    border-color: #ea580c;
  }

  .filters-container {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: start;
  }

  .filter-buttons-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }

  @media (max-width: 768px) {
    .filters-container {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    
    .filter-buttons-row {
      justify-content: center;
    }
  }
`;

const MarineReport = () => {
  const [insuranceData, setInsuranceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  // ✅ Added state to control date filter visibility (default: false)
  const [showDateFilters, setShowDateFilters] = useState(false);

  // ✅ Added contract date filters
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: ''
  });

  // ✅ Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ✅ Reset filters function
  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: ''
    });
  };

  // ✅ Toggle date filters visibility
  const toggleDateFilters = () => {
    setShowDateFilters(!showDateFilters);
  };

  const fetchInsuranceData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '100'
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // ✅ Add date filters to params
      if (filters.contractDateFrom) {
        params.append('contractDateFrom', filters.contractDateFrom);
      }
      if (filters.contractDateTo) {
        params.append('contractDateTo', filters.contractDateTo);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/insurance/marine-insurance?${params.toString()}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch insurance data');
      }

      const data = await response.json();
      const uniqueData = data.data ? data.data.filter((item, index, self) =>
        index === self.findIndex(t => t.insurance_id === item.insurance_id)
      ) : [];

      setInsuranceData(uniqueData);
      setTotalRecords(data.total || 0);
    } catch (error) {
      console.error('Error fetching insurance data:', error);
      alert('Error fetching insurance data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    if (showPendingOnly) {
      const pendingRecords = insuranceData.filter(item => 
        !item.policy_number || item.policy_number.trim() === ''
      );
      setFilteredData(pendingRecords);
    } else {
      setFilteredData(insuranceData);
    }
  }, [insuranceData, showPendingOnly]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleFilter = () => {
    setShowPendingOnly(!showPendingOnly);
  };

  // ✅ Updated clear all filters function
  const clearAllFilters = () => {
    setSearchTerm('');
    setShowPendingOnly(false);
    setFilters({
      contractDateFrom: '',
      contractDateTo: ''
    });
    setShowDateFilters(false); // Also hide the date filters section
  };

  // ✅ Updated useEffect to include filters dependency
  useEffect(() => {
    fetchInsuranceData();
  }, [searchTerm, filters, fetchInsuranceData]);

  const displayedRecords = filteredData.length;
  const pendingCount = insuranceData.filter(item => 
    !item.policy_number || item.policy_number.trim() === ''
  ).length;

  // Enhanced print function from reference file
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    let printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Marine Insurance Report</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .print-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .print-title { font-size: 20px; font-weight: bold; margin-bottom: 5px; }
            .print-subtitle { font-size: 12px; color: #666; margin-bottom: 10px; }
            .data-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 8px; }
            .data-table th { background-color: #e9ecef; border: 1px solid #dee2e6; padding: 4px 2px; text-align: left; font-weight: bold; font-size: 7px; }
            .data-table td { border: 1px solid #dee2e6; padding: 3px 2px; font-size: 7px; }
            .data-table .text-right { text-align: right; }
            .data-table .text-center { text-align: center; }
            @page { size: A4 landscape; margin: 1cm; }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Marine Insurance Report</div>
          <div class="print-subtitle">As On ${new Date().toLocaleDateString("en-IN")}</div>
        </div>
        
        <table class="data-table">
          <thead>
            <tr>
              <th>Buyer</th>
              <th>State</th>
              <th>GST No</th>
              <th>Address</th>
              <th>Seller</th>
              <th>Product</th>
              <th>Contract No.</th>
              <th>Contract Date</th>
              <th class="text-right">Quantity (MT)</th>
              <th class="text-right">Rate @USD</th>
              <th class="text-right">Amount US $</th>
              <th class="text-right">Exchange rate</th>
              <th class="text-right">Amount INR</th>
              <th class="text-right">Mark up 5%</th>
              <th class="text-right">Total Value</th>
              <th>Vessel name</th>
              <th>Load Port</th>
              <th>Destination Port</th>
              <th>Shipment Period</th>
              <th>Departure Date</th>
              <th class="text-right">Insurance Premium</th>
              <th class="text-right">GST @18%</th>
              <th class="text-right">Total Premium</th>
              <th>Policy No</th>
            </tr>
          </thead>
          <tbody>
    `;

    filteredData.forEach((insurance) => {
      const exchangeRate = insurance.amount_inr && insurance.amount_usd 
        ? (Number(insurance.amount_inr) / Number(insurance.amount_usd)).toFixed(2)
        : '-';

      printContent += `
        <tr>
          <td>${insurance.buyer_name || '-'}</td>
          <td>${insurance.buyer_state || '-'}</td>
          <td>${insurance.gst_no || '-'}</td>
          <td>${insurance.buyer_address || '-'}</td>
          <td>${insurance.supplier_name || '-'}</td>
          <td>${insurance.commodity_name || '-'}</td>
          <td>${insurance.contract_no || '-'}</td>
          <td>${insurance.contract_date ? formatDateDDMMYY(insurance.contract_date) : '-'}</td>
          <td class="text-right">${insurance.quantity ? Number(insurance.quantity).toFixed(2) : '-'}</td>
          <td class="text-right">$${insurance.rate ? Number(insurance.rate).toFixed(2) : '-'}</td>
          <td class="text-right">$${insurance.amount_usd ? Number(insurance.amount_usd).toLocaleString() : '-'}</td>
          <td class="text-right">${exchangeRate}</td>
          <td class="text-right">₹${insurance.amount_inr ? Number(insurance.amount_inr).toLocaleString() : '-'}</td>
          <td class="text-right">₹${insurance.markup ? Number(insurance.markup).toLocaleString() : '-'}</td>
          <td class="text-right">₹${insurance.total_value ? Number(insurance.total_value).toLocaleString() : '-'}</td>
          <td>${insurance.vessel_name || '-'}</td>
          <td>${insurance.loading_port || '-'}</td>
          <td>${insurance.destination_port || '-'}</td>
          <td>${insurance.departure_date && insurance.arrival_date
            ? `${formatDateDDMMYY(insurance.departure_date)} to ${formatDateDDMMYY(insurance.arrival_date)}`
            : '-'}</td>
          <td>${insurance.departure_date ? formatDateDDMMYY(insurance.departure_date) : '-'}</td>
          <td class="text-right">₹${insurance.insurance_premium ? Number(insurance.insurance_premium).toLocaleString() : '-'}</td>
          <td class="text-right">₹${insurance.gst ? Number(insurance.gst).toLocaleString() : '-'}</td>
          <td class="text-right">₹${insurance.total_premium ? Number(insurance.total_premium).toLocaleString() : '-'}</td>
          <td>${insurance.policy_number || '[Pending]'}</td>
        </tr>
      `;
    });

    printContent += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    printWindow.onload = function () {
      printWindow.print();
      printWindow.close();
    };
  };

  return (
    <>
      <style>{printStyles}</style>
      <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#0c75b2'}}>Marine Insurance Report</h1>
            <p className="text-gray-600 mt-2">Comprehensive report of all marine insurance policies</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 rounded-full text-sm font-medium" style={{backgroundColor: '#0c75b2', color: 'white'}}>
              Total Records: {totalRecords}
            </div>
            {showPendingOnly && (
              <div className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                Showing: {displayedRecords} No Policy Records
              </div>
            )}
            <button
              onClick={handlePrint}
              className="text-white px-4 py-2 rounded text-sm font-medium no-print"
              style={{backgroundColor: '#0c75b2'}}
              onMouseOver={(e) => e.target.style.backgroundColor = '#095a8a'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0c75b2'}
            >
              Print Report
            </button>
          </div>
        </div>

        {/* Search and Filter Section - Enhanced with Date Filters */}
        <div className="search-container no-print">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold" style={{color: '#0c75b2'}}>Search & Filter Records</h3>
            <div className="flex items-center gap-3">
              {/* ✅ Date Filter Toggle Button */}
              <button
                onClick={toggleDateFilters}
                className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 text-sm font-medium transition-colors"
              >
                <FaFilter className="text-blue-600" />
                Contract Date Filter
                {showDateFilters ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                {(filters.contractDateFrom || filters.contractDateTo) && (
                  <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </button>

              {(searchTerm || showPendingOnly || filters.contractDateFrom || filters.contractDateTo) && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>

          <div className="filters-container">
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by contract no, GST no, buyer name, state, supplier, product, vessel name, ports, policy number, or address..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0c75b2';
                    e.target.style.boxShadow = '0 0 0 3px rgba(12, 117, 178, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* ✅ Collapsible Date Filter Section */}
              {showDateFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200 transition-all duration-200">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FaCalendarAlt className="text-blue-600" />
                      Contract Date From
                    </label>
                    <input
                      type="date"
                      name="contractDateFrom"
                      value={filters.contractDateFrom}
                      onChange={handleFilterChange}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FaCalendarAlt className="text-blue-600" />
                      Contract Date To
                    </label>
                    <input
                      type="date"
                      name="contractDateTo"
                      value={filters.contractDateTo}
                      onChange={handleFilterChange}
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  {(filters.contractDateFrom || filters.contractDateTo) && (
                    <div className="md:col-span-2 flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                      <span className="text-sm text-blue-700 font-medium">
                        {filters.contractDateFrom && filters.contractDateTo
                          ? `Filtering contracts from ${filters.contractDateFrom} to ${filters.contractDateTo}`
                          : filters.contractDateTo
                          ? `Filtering contracts up to ${filters.contractDateTo}`
                          : `Filtering contracts from ${filters.contractDateFrom} onwards`}
                      </span>
                      <button
                        onClick={resetFilters}
                        className="px-3 py-1 text-xs bg-blue-200 text-blue-700 rounded hover:bg-blue-300 font-medium"
                      >
                        Reset Date Filters
                      </button>
                    </div>
                  )}
                </div>
              )}

              {searchTerm && (
                <p className="text-sm text-gray-600">
                  Searching in: Contract No, GST No, Buyer Name, State, Address, Supplier, Product, Vessel Name, Loading Port, Destination Port, Policy Number
                </p>
              )}
            </div>

            <div className="filter-buttons-row">
              <button
                onClick={toggleFilter}
                className={`filter-toggle-button ${showPendingOnly ? 'view-all-records' : 'view-no-policy'}`}
              >
                {showPendingOnly ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View All Records
                    <span className="ml-1 px-2 py-1 text-xs bg-white bg-opacity-20 text-white rounded-full">
                      {insuranceData.length}
                    </span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View No Policy Records
                    <span className="ml-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                      {pendingCount}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>

          {showPendingOnly && (
            <div className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-2 rounded mt-4">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Showing only records without policy numbers
            </div>
          )}
        </div>

        {/* Loading State - Original */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-indigo-500 hover:bg-indigo-400 transition ease-in-out duration-150 cursor-not-allowed" disabled="">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading insurance data...
            </div>
          </div>
        )}

        {/* Marine Insurance Contract Table - Original styling preserved */}
        {!loading && filteredData.length > 0 && (
          <div className="mb-8">
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white contract-table border border-gray-200">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Buyer</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">State of Purchase</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">GST No</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0" style={{width: '150px'}}>Address</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Seller</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Product</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Contract No.</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Contract Date</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Quantity (MT)</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Rate @USD</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Amount US $</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Exchange rate USD to INR</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Amount INR</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Mark up/Expense 5%</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Total Value</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Vessel name</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Load Port</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Destination Port</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Shipment Period</th>
                    <th className="px-4 py-2 text-left font-bold text-sm border-r border-gray-200 last:border-r-0">Departure Date</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Insurance Premium @0.01%</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">GST @18%</th>
                    <th className="px-4 py-2 text-center font-bold text-sm border-r border-gray-200 last:border-r-0">Total Premium / Rs.</th>
                    <th className="px-4 py-2 text-left font-bold text-sm">Policy No / Certificate No</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((insurance, index) => (
                    <tr key={insurance.insurance_id || index} className="hover:bg-gray-50 border-b border-gray-100">
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">{insurance.buyer_name || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">{insurance.buyer_state || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm font-mono border-r border-gray-200 last:border-r-0">{insurance.gst_no || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">
                        {insurance.buyer_address ? (
                          <span className="address-cell" title={insurance.buyer_address}>
                            {insurance.buyer_address}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">{insurance.supplier_name || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm font-medium border-r border-gray-200 last:border-r-0">{insurance.commodity_name || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm font-semibold border-r border-gray-200 last:border-r-0">{insurance.contract_no || '-'}</td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">{insurance.contract_date ? formatDateDDMMYY(insurance.contract_date) : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-semibold border-r border-gray-200 last:border-r-0">{insurance.quantity ? Number(insurance.quantity).toFixed(2) : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">${insurance.rate ? Number(insurance.rate).toFixed(2) : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-semibold border-r border-gray-200 last:border-r-0">${insurance.amount_usd ? Number(insurance.amount_usd).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">{insurance.amount_inr && insurance.amount_usd ? (Number(insurance.amount_inr) / Number(insurance.amount_usd)).toFixed(2) : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-semibold border-r border-gray-200 last:border-r-0">₹{insurance.amount_inr ? Number(insurance.amount_inr).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">₹{insurance.markup ? Number(insurance.markup).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-bold border-r border-gray-200 last:border-r-0">₹{insurance.total_value ? Number(insurance.total_value).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-left text-sm font-medium border-r border-gray-200 last:border-r-0">{insurance.vessel_name || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">{insurance.loading_port || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">{insurance.destination_port || '-'}</td>
                      <td className="px-4 py-2 text-left text-sm border-r border-gray-200 last:border-r-0">
                        {insurance.departure_date && insurance.arrival_date
                          ? `${formatDateDDMMYY(insurance.departure_date)} to ${formatDateDDMMYY(insurance.arrival_date)}`
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">{insurance.departure_date ? formatDateDDMMYY(insurance.departure_date) : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-semibold border-r border-gray-200 last:border-r-0">₹{insurance.insurance_premium ? Number(insurance.insurance_premium).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm border-r border-gray-200 last:border-r-0">₹{insurance.gst ? Number(insurance.gst).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-center text-sm font-bold border-r border-gray-200 last:border-r-0">₹{insurance.total_premium ? Number(insurance.total_premium).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2 text-left text-sm text-gray-500">
                        {insurance.policy_number || (
                          <span className="text-gray-500 font-medium">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Data Message - Original */}
        {!loading && filteredData.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {showPendingOnly 
                ? "No records found without policy numbers."
                : searchTerm || filters.contractDateFrom || filters.contractDateTo
                  ? "No marine insurance records found matching the applied filters."
                  : "No marine insurance records found."
              }
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default MarineReport;
